/**
 * CUSTOM: Storefront API Sibling Swatches
 * Added: 2026-05-18
 * Description: Batches all sibling handles from the page into one
 *   Storefront API GraphQL request, then injects tooltip-component
 *   HTML into each <siblings-api-swatches> element.
 *   Replaces all_products[handle] loops in product-grid-item.liquid,
 *   lifting the 20-unique-products-per-page limit.
 */

(function () {
  'use strict';

  // ─── Utility helpers ──────────────────────────────────────────────────────

  /**
   * Replicates Liquid | handle filter:
   * lowercase, spaces/underscores → hyphens, non-alphanumeric stripped.
   */
  function handleToClass(label) {
    if (!label || !label.trim()) return '';
    return label
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  /**
   * Converts a Storefront API amount string (e.g. "65.00") to a formatted
   * price string using the theme's existing formatMoney utility.
   * Returns empty string for null/blank input.
   */
  function formatPrice(amountString, useWithCurrency) {
    if (amountString == null || amountString === '') return '';
    const cents = Math.round(parseFloat(amountString) * 100);
    if (isNaN(cents)) return '';
    const cfg = window.theme && window.theme.storefront;
    const format =
      useWithCurrency && cfg
        ? cfg.moneyWithCurrencyFormat
        : cfg
        ? cfg.moneyFormat
        : '${{amount}}';
    return window.theme.formatMoney(cents, format);
  }

  /**
   * Normalizes one product alias from the Storefront API response into a
   * plain SiblingProduct object. Returns null for null/missing alias data.
   */
  function parseProduct(aliasData) {
    if (!aliasData) return null;

    const price = Math.round(
      parseFloat((aliasData.priceRange &&
        aliasData.priceRange.minVariantPrice &&
        aliasData.priceRange.minVariantPrice.amount) || '0') * 100
    );
    const compareAtRaw = parseFloat(
      (aliasData.compareAtPriceRange &&
        aliasData.compareAtPriceRange.maxVariantPrice &&
        aliasData.compareAtPriceRange.maxVariantPrice.amount) || '0'
    );
    const compareAtCents = Math.round(compareAtRaw * 100);
    const compareAtPrice = compareAtCents > price ? compareAtCents : null;

    const metafields = aliasData.metafields || [];
    function metaVal(namespace, key) {
      for (var i = 0; i < metafields.length; i++) {
        var m = metafields[i];
        if (m && m.namespace === namespace && m.key === key) {
          return (m.value && m.value.trim()) || null;
        }
      }
      return null;
    }

    const colorLabel = metaVal('custom', 'color_label');

    return {
      handle: aliasData.handle || '',
      url: aliasData.onlineStoreUrl || ('/products/' + (aliasData.handle || '')),
      available: !!aliasData.availableForSale,
      price: price,
      compareAtPrice: compareAtPrice,
      imageUrl: (aliasData.featuredImage && aliasData.featuredImage.url) || null,
      colorLabel: colorLabel,
      size: metaVal('custom', 'size'),
      cutline: metaVal('theme', 'cutline'),
    };
  }

  /**
   * Builds a batched Storefront API GraphQL query string.
   * Each handle gets an alias: p0, p1, p2, …
   */
  function buildQuery(handles) {
    var fields = [
      'handle',
      'onlineStoreUrl',
      'availableForSale',
      'priceRange { minVariantPrice { amount } }',
      'compareAtPriceRange { maxVariantPrice { amount } }',
      'featuredImage { url }',
      'metafields(identifiers: [' +
        '{namespace:"custom",key:"color_label"},' +
        '{namespace:"custom",key:"size"},' +
        '{namespace:"theme",key:"cutline"}' +
        ']) { namespace key value }',
    ].join('\n    ');

    var aliases = handles.map(function (handle, i) {
      return '  p' + i + ': product(handle: ' + JSON.stringify(handle) + ') {\n    ' + fields + '\n  }';
    });

    return 'query SiblingsBatch {\n' + aliases.join('\n') + '\n}';
  }

  /**
   * Builds the full <tooltip-component> HTML string for one sibling product.
   * Matches the existing DOM contract consumed by SiblingSwatches in theme.dev.js.
   */
  function buildSwatchHTML(product, index, currentHandle) {
    if (!product) return '';

    var cfg = (window.theme && window.theme.storefront) || {};
    var swatchStyle = cfg.swatchStyle || 'circle';
    var siblingStyle = cfg.siblingStyle || 'image';
    var swatchWidth = cfg.swatchWidth || 35;
    var useCurrency = !!cfg.currencyCodeEnabled;

    var colorLabel = product.colorLabel || '';
    var colorHandle = handleToClass(colorLabel);
    var tooltip = colorLabel || product.handle;

    var isCurrent = product.handle === currentHandle;
    var currentClass = isCurrent ? ' sibling__link--current' : '';
    var soldOutClass = product.available === false ? ' sibling__link--sold-out' : '';

    var priceFormatted = formatPrice(
      String(product.price / 100),
      useCurrency
    );

    var compareAtFormatted = product.compareAtPrice != null
      ? formatPrice(String(product.compareAtPrice / 100), useCurrency)
      : '';

    // data-sibling-image uses raw URL (no width params) — SiblingSwatches uses
    // it directly for the hover image swap in the card's media area.
    var imageUrl = product.imageUrl || '';

    var imgHtml = '';
    if (siblingStyle === 'image' && imageUrl) {
      var w1 = swatchWidth;
      var w15 = Math.round(swatchWidth * 1.5);
      var w2 = swatchWidth * 2;
      imgHtml =
        '<img' +
        ' src="' + imageUrl + '?width=' + w2 + '"' +
        ' srcset="' +
          imageUrl + '?width=' + w1 + ' ' + w1 + 'w,' +
          imageUrl + '?width=' + w15 + ' ' + w15 + 'w,' +
          imageUrl + '?width=' + w2 + ' ' + w2 + 'w' +
        '"' +
        ' width="' + w2 + '" height="' + w2 + '"' +
        ' loading="lazy" alt="">';
    }

    var animDelay = index * 50;

    return (
      '<tooltip-component' +
      ' class="swatches swatch__button swatch__button--' + swatchStyle + ' swatch-' + colorHandle + '"' +
      ' style="--animation-delay:' + animDelay + 'ms;--swatch:var(--' + colorHandle + ');"' +
      ' data-tooltip="' + escapeAttr(tooltip) + '"' +
      ' data-sibling-link="' + escapeAttr(product.url) + '"' +
      ' data-sibling-price="' + escapeAttr(priceFormatted) + '"' +
      ' data-sibling-compare-at-price="' + escapeAttr(compareAtFormatted) + '"' +
      ' data-sibling-cutline="' + escapeAttr(product.cutline || '') + '"' +
      ' data-sibling-image="' + escapeAttr(imageUrl) + '"' +
      ' tabindex="0">' +
      '<div class="sibling__link' + currentClass + soldOutClass + '">' +
      imgHtml +
      '</div>' +
      '</tooltip-component>'
    );
  }

  /** Escapes a string for use as an HTML attribute value (double-quoted). */
  function escapeAttr(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ─── PageBatchManager ─────────────────────────────────────────────────────

  /**
   * Collects all <siblings-api-swatches> elements that register during the
   * current microtask queue, then fires a single Storefront API request.
   */
  function PageBatchManager() {
    // Map<handle, Set<SiblingsApiSwatches>> — elements waiting for each handle
    this.pending = new Map();
    this.scheduled = false;
  }

  /**
   * Register an element and its required handles.
   * Schedules a batch dispatch on the first registration.
   */
  PageBatchManager.prototype.register = function (element, handles) {
    for (var i = 0; i < handles.length; i++) {
      var h = handles[i];
      if (!this.pending.has(h)) {
        this.pending.set(h, new Set());
      }
      this.pending.get(h).add(element);
    }
    if (!this.scheduled) {
      this.scheduled = true;
      var self = this;
      queueMicrotask(function () { self.dispatch(); });
    }
  };

  /**
   * Fire the batched GraphQL request and distribute results to elements.
   */
  PageBatchManager.prototype.dispatch = function () {
    this.scheduled = false;

    var cfg = window.theme && window.theme.storefront;
    if (!cfg || !cfg.token) {
      console.warn('[siblings-storefront] dispatch: no token — removing all elements');
      this._removeAll();
      return;
    }

    var handles = Array.from(this.pending.keys());
    if (!handles.length) return;

    console.log('[siblings-storefront] dispatch: fetching', handles.length, 'handles:', handles);

    var query = buildQuery(handles);
    var shop = cfg.shop;
    var token = cfg.token;
    var pendingSnapshot = this.pending;
    this.pending = new Map();

    fetch('https://' + shop + '/api/2026-04/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query: query }),
    })
      .then(function (res) {
        console.log('[siblings-storefront] response HTTP', res.status);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) {
        console.log('[siblings-storefront] response body:', JSON.stringify(json).substring(0, 500));
        if (!json.data || json.errors) {
          console.warn('[siblings-storefront] errors or no data — removing elements:', json.errors);
          removeAll(pendingSnapshot);
          return;
        }

        // Build handle → SiblingProduct map from aliased response
        var productMap = new Map();
        handles.forEach(function (handle, i) {
          var aliasData = json.data['p' + i];
          var product = parseProduct(aliasData);
          console.log('[siblings-storefront]', handle, '->', product ? 'found' : 'NULL');
          productMap.set(handle, product);
        });

        // Collect unique elements and render
        var renderedElements = new Set();
        pendingSnapshot.forEach(function (elements) {
          elements.forEach(function (el) {
            if (!renderedElements.has(el)) {
              renderedElements.add(el);
              el.render(productMap);
            }
          });
        });
      })
      .catch(function (err) {
        console.error('[siblings-storefront] fetch failed:', err);
        removeAll(pendingSnapshot);
      });
  };

  PageBatchManager.prototype._removeAll = function () {
    removeAll(this.pending);
    this.pending = new Map();
  };

  function removeAll(pendingMap) {
    var seen = new Set();
    pendingMap.forEach(function (elements) {
      elements.forEach(function (el) {
        if (!seen.has(el)) {
          seen.add(el);
          if (el.parentNode) el.parentNode.removeChild(el);
        }
      });
    });
  }

  // Module-level singleton — shared across all elements on the page
  var pageBatchManager = new PageBatchManager();

  // ─── SiblingsApiSwatches custom element ───────────────────────────────────

  class SiblingsApiSwatches extends HTMLElement {
    connectedCallback() {
      // Guard against double-init (e.g. infinite scroll re-connecting)
      if (this._injected) return;

      const handlesRaw = this.dataset.handles || '';
      const handles = handlesRaw.split(',').map(h => h.trim()).filter(Boolean);

      console.log('[siblings-storefront] connectedCallback handles:', handles);

      if (!handles.length) {
        console.warn('[siblings-storefront] empty handles — removing element');
        if (this.parentNode) this.parentNode.removeChild(this);
        return;
      }

      const cfg = window.theme && window.theme.storefront;
      if (!cfg || !cfg.token) {
        console.warn('[siblings-storefront] no token — removing element. cfg:', cfg);
        if (this.parentNode) this.parentNode.removeChild(this);
        return;
      }

      console.log('[siblings-storefront] registering', handles.length, 'handles');
      pageBatchManager.register(this, handles);
    }

    render(productMap) {
      this._injected = true;

      const handlesRaw = this.dataset.handles || '';
      const handles = handlesRaw.split(',').map(h => h.trim()).filter(Boolean);
      const currentHandle = this.dataset.current || '';

      let html = '';
      handles.forEach((handle, i) => {
        const product = productMap.get(handle);
        if (product) {
          html += buildSwatchHTML(product, i, currentHandle);
        }
      });

      if (!html) {
        if (this.parentNode) this.parentNode.removeChild(this);
        return;
      }

      this.innerHTML = html;

      // Re-initialize ProductSiblings hover behavior.
      // ProductSiblings.connectedCallback() is called directly because it
      // initializes SiblingSwatches (hover/click binding) only if
      // [data-sibling-link] children exist — which they do now after injection.
      // This avoids modifying theme.dev.js.
      const pis = this.closest('product-item-siblings');
      if (pis && typeof pis.connectedCallback === 'function') {
        pis.connectedCallback();
      }
    }
  }

  if (!customElements.get('siblings-api-swatches')) {
    customElements.define('siblings-api-swatches', SiblingsApiSwatches);
  }
})();
